import { useProductStore } from '../stores/productStore';
import { usePageStore } from '../stores/pageStore';
import { useBlogStore } from '../stores/blogStore';
import gql from 'graphql-tag';
import { useApolloClient } from '@vue/apollo-composable';
import { ref } from 'vue';
import type { ProductModel } from '../types/shopify';
import { productFragment } from '../composables/shopify';

export interface SearchResult {
  type: 'suggestion' | 'product' | 'collection' | 'article' | 'page';
  title: string;
  handle?: string;
  url?: string;
  excerpt?: string;
  image?: string;
  id?: string;
  blog?: string;
  product?: ProductModel;
  page?: any;
  collection?: any;
  article?: any;
}

const allowedBlogs = ['news', 'faq'];

export const searchOpen = ref(false);

export function useSearch() {
  const productStore = useProductStore();
  const pageStore = usePageStore();
  const blogStore = useBlogStore();
  const { client } = useApolloClient();

  const typeToPath = {
    product: 'products',
    collection: 'shop',
    article: 'blogs/news',
    page: '',
  };

  const PREDICTIVE_SEARCH_QUERY = gql`
    query predictiveSearch($query: String!, $types: [PredictiveSearchType!]) {
      predictiveSearch(query: $query, types: $types) {
        queries { text }
        products { ...product seo { title description } }
        articles {
          id title handle excerpt content blog { handle } image { src }
          seo { title description }
        }
        pages {
          id title handle seo { title description } customBackendpage: metafield(namespace: "custom", key: "backendpage") {
                value
              } 
        }
        collections {
          id title handle description image { url }
          seo { title description }
        }
      }
    }
    ${productFragment}
  `;

  const SEARCH_QUERY = gql`
  query search($query: String!, $first: Int, $types: [SearchType!]!) {
    search(query: $query, first: $first, types: $types) {
      edges {
        node {
          __typename
          ... on Page {
            id 
            title 
            handle 
            seo {
              title 
              description
            }
            customBackendpage: metafield(namespace: "custom", key: "backendpage") {
              value
            }
          }
          ... on Article {
            id
            title
            handle
            content
            excerpt
            blog {
              handle
            }
          }
          # Här "sprider" du hela product-fragmentet.
          ... on Product {
            ...product
          }
        }
      }
    }
  }
  # Ta med fragmentet sist
  ${productFragment}
`;
  
  
  
  const tokenize = (text: string): string[] => text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[^a-z0-9åäö]/gi, ' ')
    .split(/\s+/)
    .filter(Boolean);

  const containsAllTerms = (haystack: string[], terms: string[]) =>
    terms.every(term => haystack.some(token => token.includes(term)));

  const search = async (query: string): Promise<SearchResult[]> => {
      const trimmedQuery = query.trim().toLowerCase();
      const terms = tokenize(trimmedQuery);
      if (!terms.length) return [];
  
      const rawResults: SearchResult[] = [];
  
      const resultExists = (id?: string) => rawResults.some(r => r.id === id);

    try {
      const { data: predictive } = await client.query({
        query: PREDICTIVE_SEARCH_QUERY,
        variables: { query: trimmedQuery, types: ['PRODUCT', 'PAGE', 'ARTICLE', 'COLLECTION', 'QUERY'] },
      });

      for (const suggestion of predictive.predictiveSearch.queries || []) {
        rawResults.push({ type: 'suggestion', title: suggestion.text });
      }

      // PRODUCTS
      for (const node of predictive.predictiveSearch.products || []) {
        const haystack = tokenize([
          node.title, node.description, node.descriptionHtml,
          ...(node.tags || []), node.productType, node.handle,
          node.seo?.title, node.seo?.description
        ].filter(Boolean).join(' '));

        if (containsAllTerms(haystack, terms)) {
          rawResults.push({
            type: 'product', title: node.title, handle: node.handle,
            url: `/${typeToPath.product}/${node.handle}`, id: node.id,
            excerpt: node.description?.slice(0, 150), image: node.featuredImage?.url,
            product: node
          });
          productStore.setProductByHandle(node.handle, node);
        }
      }

      // ARTICLES
      for (const node of predictive.predictiveSearch.articles || []) {
        const haystack = tokenize([
          node.title, node.content, node.excerpt, node.handle,
          node.blog?.handle, node.seo?.title, node.seo?.description
        ].filter(Boolean).join(' '));

        if (containsAllTerms(haystack, terms) && allowedBlogs.includes(node.blog?.handle)) {
          blogStore.setArticleByHandle(node);
          const full = await blogStore.getArticleByHandle(node.handle);
          rawResults.push({
            type: 'article', title: node.title, handle: node.handle,
            url: `/${typeToPath.article}/${node.handle}`, image: node.image?.src,
            excerpt: node.excerpt, id: node.id, blog: node.blog?.handle,
            article: full
          });
        }
      }

      // PAGES 
      for (const node of predictive.predictiveSearch.pages || []) {
        const haystack = tokenize([
          node.title, node.handle, node.seo?.title, node.seo?.description
        ].filter(Boolean).join(' '));

        //console.log('checking page ', node)
        if (containsAllTerms(haystack, terms) && node.customBackendpage?.value !== 'true' && !resultExists(node.id)) {
          const full = await pageStore.fetchPageByID(node.id);
          //console.log('fetched page:', full);          
            rawResults.push({
              type: 'page', title: node.title, handle: node.handle,
              url: `/${node.handle}`, id: node.id, page: full
            });          
        }
      }

      // COLLECTIONS
      for (const node of predictive.predictiveSearch.collections || []) {
        const haystack = tokenize([
          node.title, node.handle, node.description, node.seo?.title, node.seo?.description
        ].filter(Boolean).join(' '));

        if (containsAllTerms(haystack, terms)) {
          let fetchedCollection = await productStore.fetchCollectionById(node.id);
          rawResults.push({
            type: 'collection', title: node.title, handle: node.handle,
            url: `/${typeToPath.collection}/${node.handle}`, id: node.id,
            excerpt: node.description?.slice(0, 150), image: node.image?.url,
            collection: fetchedCollection
          });
        }
      }

      // SECONDARY SEARCH for full SEO/body match (especially for Pages and Articles)
      const { data: deep } = await client.query({
        query: SEARCH_QUERY,
        variables: { query: trimmedQuery, types: ['PAGE', 'ARTICLE', 'PRODUCT'], first: 50 },
      });

      // PAGES
      for (const edge of deep.search.edges || []) {
        const node = edge.node;
        if (node.__typename === 'Page') {
          //console.log('checking page ', node);
          // Don't show/check hidden backend pages in search results or existing results
          if(node.customBackendpage?.value !== 'true' && !resultExists(node.id)) {
            const page = await pageStore.fetchPageByID(node.id);
              if(page)
              rawResults.push({
                type: 'page', title: page.title, handle: page.handle,
                url: `/${page.handle}`, id: page.id, page
              });
            }
          
        }

        if(node.__typename === 'Product') {
          // Hantera "Product" i secondary search
          if (!resultExists(node.id)) {
            // 1) Lägg till i store
            productStore.setProductByHandle(node.handle, node)
      
            // 2) Lägg till i rawResults
            rawResults.push({
              type: 'product',
              title: node.title,
              handle: node.handle,
              url: `/${typeToPath.product}/${node.handle}`, // 'products'
              id: node.id,
              excerpt: node.description?.slice(0, 150),
              product: node
            })
          }
        }

        // ARTICLES
        if (node.__typename === 'Article') {
          if (!allowedBlogs.includes(node.blog?.handle)){
            
            // Show pages that draws content from a blog
            let articlePage = await pageStore.fetchPageByHandle(node.blog.handle);
            //console.log('found the article page:', articlePage);
            if (articlePage && !resultExists(articlePage.id) && articlePage.customBackendpage?.value !== 'true') {
              rawResults.push({
                type: 'page', title: articlePage.title, handle: articlePage.handle,
                url: `/${articlePage.handle}`, id: articlePage.id, page: articlePage
              });
            }
          }else{
            const full = await blogStore.getArticleByHandle(node.handle);
            if (full && !rawResults.some(r => r.id === full.id)) {
              rawResults.push({
                type: 'article', title: full.title, handle: full.handle,
                url: `/${typeToPath.article}/${full.handle}`, id: full.id,
                blog: node.blog?.handle, article: full
              });
            }
          }
        }
      }

    } catch (error) {
      console.error('Combined search error:', error);
    }

    const priority = {
      suggestion: 0, product: 1, collection: 2, article: 3, page: 4
    };

    return rawResults.sort((a, b) => {
      const t = priority[a.type] - priority[b.type];
      if (t !== 0) return t;
      if (a.blog && b.blog) return a.blog.localeCompare(b.blog);
      return 0;
    });
  };

  return { search };
}
